import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { CardConfiguration } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { languageSelector, organisationConfigurationSelector, organisationTagSelector } from 'src/app/global/store/global.selectors';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { ErrorSeverity, InformationalDialogComponent } from 'src/app/global/components/dialogs/informational-dialog/informational-dialog.component';
import { ResidenceService } from 'src/app/residence/service/residence.abstract.service';
import { ProfileAddress, getResidenceDisplayState, needsReview as residenceNeedsReview } from 'src/app/residence/residence.model';
import { GeoidAddressModalComponent } from 'src/app/global/components/forms/geoid-address/modal/geoid-address-modal.component';
import { CardAction, CardSearchResponse, CardType, CardVerificationResponse, RenewCardPayload, SubscribeCardPayload, UserCardFE, UserRecord } from '../../user.model';
import { UserService } from '../../service/user.abstract.service';

@Component({
    selector: 'app-create-card-dialog',
    templateUrl: './create-card-dialog.component.html',
    styleUrls: ['./create-card-dialog.component.scss'],
})
export class CreateCardDialogComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();
    CardAction = CardAction;

    userRecordLoaded = false;
    processingSubmit = false;

    public stepIndex = 0;
    public steps: string[] = [];
    public subTypes = [];
    public instructionsStepsInfo: string;
    public instructionsStepsConfirmation: string;

    cardConfig: CardConfiguration;
    cardLabel: string;
    cardIcon: string;

    // Chantier 2: cascade notice for a CITIZEN/primary-card renewal that auto-renews other cards
    // (platform.primaryCard.propagateToEnabledCards). A target already mid-renewal is shown struck
    // through instead, since the backend cascade skips it (see approveRenewCard's AUTOPROPAGATE guard).
    cascadeCards: { cardType: string; label: string; alreadyRenewing: boolean }[] = [];

    // Address selector
    userAddresses: ProfileAddress[] = [];
    userAddressesLoaded = false;
    selectedAddressIndex = 0;
    subTypeLocked = false;
    searchResult: CardSearchResponse | null = null;
    searchLoading = false;
    searchCompleted = false;
    useSearchFlow = false;

    // Verification state
    verification: CardVerificationResponse | null = null;
    verificationLoading = false;
    selectedExternalId: string | null = null;
    selectedCardNumber: string | null = null;
    verificationStep = false;
    orgTag: string;

    public form: any = new FormGroup({
        picture: new FormControl('', [Validators.required]),
        firstName: new FormControl('', [Validators.required, Validators.minLength(2)]),
        lastName: new FormControl('', [Validators.required, Validators.minLength(2)]),
        sex: new FormControl('', [Validators.required]),
        address: new FormControl('', [Validators.required]),
        birthDate: new FormControl('', [Validators.required]),
        cardType: new FormControl('', [Validators.required]),
        subType: new FormControl('', []),
    });

    constructor(
        private translateService: TranslateService,
        private userService: UserService,
        private messageService: MessageService,
        private dialogRef: MatDialogRef<CreateCardDialogComponent>,
        private dialog: MatDialog,
        private store: Store<State>,
        private residenceService: ResidenceService,
        @Inject(MAT_DIALOG_DATA) public data: { card: Partial<UserCardFE>; action: CardAction; userRecord: UserRecord },
    ) {}

    userId: string;
    ngOnInit() {
        combineLatest([this.store.select(languageSelector), this.store.select(organisationConfigurationSelector)])
            .pipe(
                map(([lang, orgConfig]) => {
                    this.initForm();
                    (this.translateService.instant('pages.user-cards.create.steps.information.heading'), this.translateService.instant('pages.user-cards.create.steps.confirmation.heading'));

                    this.cardConfig = orgConfig?.support?.cards?.find((c) => c.type === this.data.card.cardType);
                    this.instructionsStepsInfo = this.cardConfig?.identity?.instructions?.steps?.info?.[lang] || '';
                    this.instructionsStepsConfirmation = this.cardConfig?.identity?.instructions?.steps?.confirmation?.[lang] || '';
                    if (this.cardConfig?.availableSubTypes?.length > 0) {
                        const subTypes = [];
                        this.cardConfig?.availableSubTypes.forEach((availableSubType) => {
                            const subType = orgConfig?.subTypes?.find((s) => s.tag === availableSubType);
                            subTypes.push({ label: subType?.name?.[lang], value: subType.tag });
                        });
                        this.subTypes = subTypes;
                        this.form.controls.subType.setValidators(Validators.required);
                    }
                    this.cardLabel = this.cardConfig?.identity?.label?.[lang] ?? this.data.card.cardType;
                    this.cardIcon = this.cardConfig?.identity?.icon || 'icon-my-card';

                    this.loadCascadeCards(orgConfig, lang);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.store
            .select(organisationTagSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((tag) => {
                this.orgTag = tag;
                this.useSearchFlow = (tag === 'STECATHERINE' && this.data.card.cardType === CardType.LIBRARY) || tag === 'BROSSARD';
            });

        this.form.patchValue({
            firstName: this.data.userRecord.firstName || '',
            lastName: this.data.userRecord.lastName || '',
            sex: this.data.userRecord.sex || '',
            picture: this.data.userRecord.picture || '',
            birthDate: this.data.userRecord.birthDate || '',
        });
        this.userId = this.data.userRecord.id;
        if (this.useSearchFlow && this.data.action === CardAction.SUBSCRIBE) {
            this.searchBeforeCreate();
        }
        if (this.data.userRecord.addresses && this.data.userRecord.addresses[0]) {
            this.form.patchValue({ address: this.data.userRecord.addresses[0] });
        }
        this.userRecordLoaded = true;
        this.loadUserAddresses();
    }

    public initForm() {
        this.form.patchValue({ cardType: this.data.card.cardType });
    }

    public closeDialog() {
        this.dialogRef.close();
    }

    public next() {
        this.stepIndex += 1;
    }

    public back() {
        this.stepIndex -= 1;
    }

    public validateInfoStep() {
        return (
            this.userAddresses.length > 0 &&
            this.form.controls.address.valid &&
            this.form.controls.firstName.valid &&
            this.form.controls.lastName.valid &&
            this.form.controls.cardType.valid &&
            this.form.controls.sex.valid &&
            this.form.controls.birthDate.valid &&
            this.form.controls.picture.valid &&
            (this.subTypes.length === 0 || this.form.controls.subType?.valid)
        );
    }

    public validateCardForm() {
        return this.form.valid;
    }

    public submit() {
        if (this.useSearchFlow && this.data.action === CardAction.SUBSCRIBE && !this.searchCompleted) {
            this.searchBeforeCreate();
            return;
        }

        // Pre-create verification for LIBRARY cards in STECATHERINE (create only, not renew)
        const needsVerification = this.orgTag === 'STECATHERINE' && this.data.card?.cardType === CardType.LIBRARY && this.data.action === CardAction.SUBSCRIBE;

        if (needsVerification && !this.verificationStep) {
            this.verifyBeforeCreate();
            return;
        }

        // Block submission if duplicate blocked
        if (this.verification?.verificationType === 'DUPLICATE_BLOCKED') {
            return;
        }

        this.processingSubmit = true;

        const payload: any = {
            cardType: this.form.get('cardType').value,
            address: this.form.get('address').value,
            firstName: this.form.get('firstName').value,
            lastName: this.form.get('lastName').value,
            photo: this.form.get('picture').value,
            sex: this.form.get('sex').value,
            birthDate: this.form.get('birthDate').value,
            proofOfIdentity: [],
        };
        if (this.form.get('subType').value) {
            payload.subType = this.form.get('subType').value.value;
        }

        // Pass selectedExternalId if verification matched an existing patron
        if (this.selectedExternalId) {
            payload.selectedExternalId = this.selectedExternalId;
        }
        if (this.selectedCardNumber) {
            payload.selectedCardNumber = this.selectedCardNumber;
        }

        if (this.data.action === CardAction.RENEW) {
            payload._id = this.data.card._id;
            payload.userId = this.userId;
            this.submitRenewal(payload);
        } else {
            this.submitSubscription(payload, this.userId);
        }
    }

    public searchBeforeCreate(): void {
        this.searchLoading = true;
        const payload = {
            firstName: this.data.userRecord.firstName,
            lastName: this.data.userRecord.lastName,
            birthDate: this.data.userRecord.birthDate,
            sex: this.data.userRecord.sex,
            address: this.data.userRecord.addresses?.[0] ?? null,
        };

        this.userService
            .searchBeforeCreate(this.userId, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.searchResult = result;
                    this.searchLoading = false;
                    this.searchCompleted = true;

                    if (result.status === 'LINKED') {
                        this.dialogRef.close(true);
                    }

                    if (result.status === 'NOT_FOUND') {
                        this.searchResult = null;
                    }
                },
                error: () => {
                    this.searchLoading = false;
                    this.searchCompleted = true;
                    this.searchResult = null;
                },
            });
    }

    public addFoundCard(): void {
        const cardNumber = this.searchResult?.card?.code || this.searchResult?.card?.number;
        this.searchResult = null;
        this.searchCompleted = false;
        this.searchLoading = true;
        const payload = {
            firstName: this.data.userRecord.firstName,
            lastName: this.data.userRecord.lastName,
            birthDate: this.data.userRecord.birthDate,
            cardNumber: cardNumber,
        };
        this.userService
            .searchBeforeCreate(this.userId, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.searchResult = result;
                    this.searchLoading = false;
                    this.searchCompleted = true;
                    if (result.status === 'LINKED') {
                        this.dialogRef.close(true);
                    }
                },
                error: () => {
                    this.searchLoading = false;
                    this.searchCompleted = true;
                },
            });
    }

    private verifyBeforeCreate(): void {
        this.verificationLoading = true;
        this.verificationStep = true;

        const cardData = {
            firstName: this.form.value.firstName,
            lastName: this.form.value.lastName,
            birthDate: this.form.value.birthDate,
            sex: this.form.value.sex,
        };

        this.userService
            .adminPreCreateVerification(this.userId, cardData)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.verification = result;
                    this.verificationLoading = false;

                    if (result.verificationType === 'EXACT_MATCH' && result.matches?.length > 0) {
                        this.selectedExternalId = result.matches[0].externalId;
                        this.selectedCardNumber = result.matches[0].code || result.matches[0].number;
                    } else if (result.verificationType === 'PARTIAL_MATCH') {
                        this.selectedExternalId = null;
                        this.selectedCardNumber = null;
                    }
                },
                error: () => {
                    this.verificationLoading = false;
                    this.verification = { verificationType: 'NO_MATCH', matches: [] } as CardVerificationResponse;
                },
            });
    }

    public backFromVerification(): void {
        this.verificationStep = false;
        this.verification = null;
        this.verificationLoading = false;
        this.selectedExternalId = null;
        this.selectedCardNumber = null;
    }

    public submitSubscription(payload: SubscribeCardPayload, userId: string) {
        this.userService
            .postSubscribeCard(payload, userId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.closeDialog();
                    this.processingSubmit = false;
                    this.messageService.success(this.translateService.instant('pages.user-cards.create.messages.success'));
                },
                (error) => {
                    this.processingSubmit = false;
                    if (error.status === 409) {
                        this.dialog
                            .open(InformationalDialogComponent, {
                                panelClass: 'bciti-error-dialog',
                                data: {
                                    title: this.translateService.instant('pages.user-cards.create.errors.createConflictSubject'),
                                    body: this.translateService.instant('pages.user-cards.create.errors.createConflictDescription', {
                                        cardEmail: error.error?.messages[0]?.split(':')[1]?.trim() || '',
                                    }),
                                    errorSeverity: ErrorSeverity.Error,
                                },
                            })
                            .afterClosed()
                            .subscribe(() => {
                                this.dialogRef.close();
                            });
                    } else {
                        this.messageService.error(this.translateService.instant('pages.user-cards.create.messages.error'));
                    }
                },
            );
    }

    // Fetches a fresh wallet (not the parent list's possibly-stale copy) so this reflects whichever
    // cascade targets already have a renewal in flight right now, not at whatever point the admin
    // console's card list was last loaded.
    private loadCascadeCards(orgConfig: any, lang: string): void {
        this.cascadeCards = [];

        const isPrimaryCardRenewal = this.data.action === CardAction.RENEW && orgConfig?.primaryCard?.propagateToEnabledCards && this.data.card.cardType === orgConfig.primaryCard.type;

        const enableCards: string[] = this.cardConfig?.enableCards;
        if (!isPrimaryCardRenewal || !enableCards?.length) {
            return;
        }

        this.userService
            .getWallet(this.data.userRecord.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe((wallet) => {
                this.cascadeCards = enableCards.map((cardType) => {
                    const targetCardConfig = orgConfig.support?.cards?.find((c) => c.type === cardType);
                    const targetCard = wallet.cards?.find((c) => c.cardType === cardType);
                    return {
                        cardType,
                        label: targetCardConfig?.identity?.label?.[lang] ?? cardType,
                        alreadyRenewing: !!targetCard?.renewal,
                    };
                });
            });
    }

    public submitRenewal(payload: RenewCardPayload) {
        this.userService
            .patchRenewalCard(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.closeDialog();
                    this.processingSubmit = false;
                    this.messageService.success(this.translateService.instant('pages.user-cards.renew.messages.success'));
                },
                (error) => {
                    this.processingSubmit = false;
                    this.messageService.error(this.translateService.instant('pages.user-cards.renew.messages.error'));
                },
            );
    }

    private loadUserAddresses(): void {
        const addresses = this.data.userRecord.addresses || [];
        this.residenceService
            .getCitizenResidences(this.userId)
            .pipe(takeUntil(this.destroy$))
            .subscribe((residences) => {
                const matched = new Set<string>();
                this.userAddresses = addresses.map((addr) => {
                    const addrStreet = (addr.streetName || addr.street || '').replace(/\s+/g, '').toLowerCase();
                    const addrPostCode = (addr.postCode || '').replace(/\s+/g, '').toLowerCase();
                    const addrNumber = String(addr.number || '');
                    const matchingResidences = residences.filter(
                        (r) =>
                            !matched.has(r._id) &&
                            (r.address.streetName || r.address.street || '').replace(/\s+/g, '').toLowerCase() === addrStreet &&
                            String(r.address.number || '') === addrNumber &&
                            (r.address.postCode || '').replace(/\s+/g, '').toLowerCase() === addrPostCode,
                    );
                    matchingResidences.forEach((r) => matched.add(r._id));
                    const orgLinks = matchingResidences.map((r) => ({
                        tag: r.organisation,
                        name: r.organisation,
                        isPrimary: r.isPrimary,
                        verificationStatus: r.verification.status,
                        residenceId: r._id,
                        displayState: getResidenceDisplayState(r),
                        needsReview: residenceNeedsReview(r),
                        expirationDate: r.expirationDate,
                        subType: r.subType,
                    }));
                    return {
                        address: addr,
                        displayName: matchingResidences[0]?.name ?? (addr as any).name ?? `${addr.streetName}, ${addr.city}`,
                        orgLinks,
                        isCasual: orgLinks.length === 0,
                        isVerified: orgLinks.some((link) => link.verificationStatus === 'APPROVED'),
                    };
                });
                // Pre-select current address
                this.userAddressesLoaded = true;
                this.preselectAddress();
                this.syncSubTypeFromAddress(this.userAddresses[this.selectedAddressIndex]);
            });
    }

    private preselectAddress(): void {
        const currentAddr = this.form.get('address').value;
        if (!currentAddr || this.userAddresses.length === 0) return;
        const idx = this.userAddresses.findIndex((pa) => {
            const a = pa.address;
            return (
                (a.streetName || a.street || '').replace(/\s+/g, '').toLowerCase() === (currentAddr.streetName || currentAddr.street || '').replace(/\s+/g, '').toLowerCase() &&
                String(a.number || '') === String(currentAddr.number || '') &&
                (a.postCode || '').replace(/\s+/g, '').toLowerCase() === (currentAddr.postCode || '').replace(/\s+/g, '').toLowerCase()
            );
        });
        if (idx >= 0) this.selectedAddressIndex = idx;
    }

    onAddressSelected(index: number): void {
        this.selectedAddressIndex = index;
        const pa = this.userAddresses[index];
        if (pa?.address) {
            this.form.patchValue({ address: pa.address });
        }
        this.syncSubTypeFromAddress(pa);
    }

    private syncSubTypeFromAddress(pa: ProfileAddress): void {
        if (!pa || !this.subTypes?.length) return;

        const residenceSubType = pa.orgLinks?.[0]?.subType;
        if (residenceSubType) {
            const match = this.subTypes.find((st) => st.value === residenceSubType);
            if (match) {
                this.form.controls.subType.setValue(match);
            }
            this.subTypeLocked = true;
        } else {
            this.subTypeLocked = false;
        }
    }

    onAddNewAddress(): void {
        this.dialog
            .open(GeoidAddressModalComponent, { data: { showSubTypeSelector: true } })
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((result) => {
                if (result) {
                    const address = result.address;
                    const subType = result.subType || 'RESIDENT';
                    this.store
                        .select(organisationTagSelector)
                        .pipe(
                            take(1),
                            map((orgTag) => ({ address, orgTag })),
                        )
                        .subscribe(({ orgTag }) => {
                            this.residenceService
                                .createResidence({
                                    userId: this.userId,
                                    address,
                                    organisation: orgTag,
                                    subType,
                                })
                                .pipe(takeUntil(this.destroy$))
                                .subscribe({
                                    next: () => {
                                        this.loadUserAddresses();
                                        this.form.patchValue({ address });
                                        this.messageService.success(this.translateService.instant('pages.profile.addresses.addSuccess'));
                                    },
                                    error: () => {
                                        this.messageService.error(this.translateService.instant('pages.residences.toast.error'));
                                    },
                                });
                        });
                }
            });
    }

    formatDate(date: string): string {
        if (!date) {
            return '';
        }
        let dateString = date;
        if (date.includes('T')) {
            dateString = date.split('T')[0];
        }
        const [year, month, day] = dateString.split('-').map(Number);
        const tempDate = new Date(year, month - 1, day);
        const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Intl.DateTimeFormat(`${this.translateService.currentLang}-CA`, options).format(tempDate);
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
